import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  ParamMap,
  Router,
} from '@angular/router';
import { filter, map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

/**
 * Service for managing the document title based on route information or explicit setting.
 */
@Injectable({ providedIn: 'root' })
export class TitleService {
  /**
   * Constructs the TitleService.
   * @param router - The Angular Router instance.
   * @param activatedRoute - The Angular ActivatedRoute instance.
   * @param titleService - The Angular Title service for setting the document title.
   * @param translateService - The ngx-translate service for title translation.
   */
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService
  ) {}

  /**
   * Sets the document title based on route data or an explicit title.
   * @param title - An optional string to set as the title. If not provided, the title is derived from the route.
   */
  public setTitle(title?: string): void {
    if (title) {
      this.titleService.setTitle(title);
    } else {
      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd
          ),
          map(() => this.getLastChildRoute(this.activatedRoute)),
          map(route => this.getTitleFromRoute(route)),
          map(title => this.translateTitle(title))
        )
        .subscribe(translatedTitle =>
          this.titleService.setTitle(translatedTitle)
        );
    }
  }

  /**
   * Recursively retrieves the last child route.
   * @param route - The current activated route.
   * @returns The last child route.
   */
  private getLastChildRoute(route: ActivatedRoute): ActivatedRoute {
    let currentRoute = route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    return currentRoute;
  }

  /**
   * Retrieves the title from route data and replaces parameters with their values.
   * @param route - The route from which to retrieve the title.
   * @returns The title string with replaced parameters.
   */
  private getTitleFromRoute(route: ActivatedRoute): string {
    let title = route.snapshot.data['title'] || 'Default Title';
    const paramMap = route.snapshot.paramMap;
    const queryParams = route.snapshot.queryParamMap;

    title = this.replaceParamsInTitle(title, paramMap);
    title = this.replaceParamsInTitle(title, queryParams);

    return title;
  }

  /**
   * Replaces parameters in the title with their corresponding values from the map.
   * @param title - The title string with parameters.
   * @param params - The map of parameters to replace.
   * @returns The title string with parameters replaced by their values.
   */
  private replaceParamsInTitle(title: string, params: ParamMap): string {
    params.keys.forEach(key => {
      const value = params.get(key);
      if (value) {
        title = title.replace(`:${key}`, value);
      }
    });
    return title;
  }

  /**
   * Translates the title and handles special cases.
   * @param title - The title string to translate.
   * @returns The translated title.
   */
  private translateTitle(title: string): string {
    const splitTitle = title.split(' ');
    splitTitle[0] = this.translateService.instant(splitTitle[0]);

    if (splitTitle[0] === 'labelsHeadlines.login') {
      splitTitle[0] = 'Login';
    }

    return splitTitle.join(' ');
  }
}
